import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workOrdersAPI, processesAPI, assignmentsAPI, companiesAPI, qcAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const WorkOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showQcModal, setShowQcModal] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [pdfViewerData, setPdfViewerData] = useState(null);
  const { isAdmin, isAdminOrTis, isImalatci } = useAuth();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [orderRes, companiesRes] = await Promise.all([
        workOrdersAPI.get(id),
        companiesAPI.getAll()
      ]);
      setWorkOrder(orderRes.data.workOrder);
      setCompanies(companiesRes.data.companies);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteProcess = async (processId) => {
    try {
      const response = await processesAPI.complete(processId);
      if (response.data.requiresQc) {
        setSelectedProcess(response.data.process);
        setShowQcModal(true);
      } else {
        fetchData();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Hata oluştu');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      qc_waiting: 'bg-yellow-100 text-yellow-700',
      qc_approved: 'bg-green-100 text-green-700',
      qc_rejected: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Bekliyor',
      in_progress: 'Devam Ediyor',
      completed: 'Tamamlandı',
      qc_waiting: 'QC Bekliyor',
      qc_approved: 'QC Onaylandı',
      qc_rejected: 'QC Reddedildi'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!workOrder) {
    return <div className="text-center py-8 text-gray-500">İş emri bulunamadı</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/work-orders')}
            className="text-blue-600 hover:underline mb-2"
          >
            ← İş Emirlerine Dön
          </button>
          <h1 className="text-2xl font-bold text-gray-800">{workOrder.jobNumber}</h1>
          <p className="text-gray-500">{workOrder.mainCompany}</p>
        </div>
        <span className={`px-4 py-2 rounded-lg font-medium ${getStatusColor(workOrder.status)}`}>
          {getStatusLabel(workOrder.status)}
        </span>
      </div>

      {/* İş Bilgileri */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="grid grid-cols-4 gap-6">
          <div>
            <p className="text-gray-500 text-sm">Teslim Tarihi</p>
            <p className="font-medium">{new Date(workOrder.deliveryDate).toLocaleDateString('tr-TR')}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Kalan Süre</p>
            <p className={`font-medium ${workOrder.remainingDays < 3 ? 'text-red-600' : 'text-green-600'}`}>
              {workOrder.remainingDays > 0 ? `${workOrder.remainingDays} gün` : 'Gecikmiş'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">İlerleme</p>
            <p className="font-medium">%{workOrder.progressPercent}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Oluşturan</p>
            <p className="font-medium">{workOrder.creator?.name}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="h-4 rounded-full bg-blue-600"
              style={{ width: `${workOrder.progressPercent}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Atamalar */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">Atanan Firmalar</h2>
          {isAdminOrTis() && (
            <button
              onClick={() => setShowAssignModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + Firma Ata
            </button>
          )}
        </div>

        {workOrder.Assignments?.length > 0 ? (
          <div className="space-y-3">
            {workOrder.Assignments.map((assignment) => (
              <div key={assignment.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{assignment.Company?.name}</p>
                  <p className="text-sm text-gray-500">{assignment.Company?.authorizedPerson}</p>
                </div>
                <div className="flex space-x-2">
                  {assignment.contractFile && (
                    <>
                      <button
                        onClick={() => setPdfViewerData({
                          url: assignmentsAPI.viewContract(assignment.id),
                          downloadUrl: assignmentsAPI.downloadContract(assignment.id),
                          title: `${assignment.Company?.name} - Sözleşme`
                        })}
                        className="px-3 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                      >
                        Görüntüle
                      </button>
                      <a
                        href={assignmentsAPI.downloadContract(assignment.id)}
                        className="px-3 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200"
                      >
                        İndir
                      </a>
                    </>
                  )}
                  {isAdmin() && (
                    <button
                      onClick={async () => {
                        if (window.confirm('Bu atamayı kaldırmak istiyor musunuz?')) {
                          await assignmentsAPI.delete(assignment.id);
                          fetchData();
                        }
                      }}
                      className="px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                    >
                      Kaldır
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Henüz firma atanmamış</p>
        )}
      </div>

      {/* Süreçler */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">Süreçler</h2>
          {isAdminOrTis() && (
            <button
              onClick={() => setShowProcessModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + Süreç Ekle
            </button>
          )}
        </div>

        {workOrder.processes?.length > 0 ? (
          <div className="space-y-3">
            {workOrder.processes.sort((a, b) => a.orderIndex - b.orderIndex).map((process) => (
              <div key={process.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full font-medium">
                      {process.orderIndex}
                    </span>
                    <div>
                      <p className="font-medium">{process.name}</p>
                      {process.requiresQc && (
                        <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded">QC Gerekli</span>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(process.status)}`}>
                    {getStatusLabel(process.status)}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{ width: `${process.progressPercent}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div>
                    {process.plannedStart && process.plannedEnd && (
                      <span>
                        Plan: {new Date(process.plannedStart).toLocaleDateString('tr-TR')} -
                        {new Date(process.plannedEnd).toLocaleDateString('tr-TR')}
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {isImalatci() && process.status !== 'completed' && process.status !== 'qc_approved' && (
                      <button
                        onClick={() => handleCompleteProcess(process.id)}
                        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Tamamla
                      </button>
                    )}
                    {isAdmin() && (
                      <button
                        onClick={async () => {
                          if (window.confirm('Bu süreci silmek istiyor musunuz?')) {
                            await processesAPI.delete(process.id);
                            fetchData();
                          }
                        }}
                        className="px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                      >
                        Sil
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Henüz süreç eklenmemiş</p>
        )}
      </div>

      {/* Modals */}
      {showAssignModal && (
        <AssignModal
          workOrderId={id}
          companies={companies}
          onClose={() => setShowAssignModal(false)}
          onSave={fetchData}
        />
      )}

      {showProcessModal && (
        <ProcessModal
          workOrderId={id}
          onClose={() => setShowProcessModal(false)}
          onSave={fetchData}
        />
      )}

      {showQcModal && selectedProcess && (
        <QcRequestModal
          process={selectedProcess}
          onClose={() => {
            setShowQcModal(false);
            setSelectedProcess(null);
          }}
          onSave={fetchData}
        />
      )}

      {pdfViewerData && (
        <PdfViewerModal
          url={pdfViewerData.url}
          downloadUrl={pdfViewerData.downloadUrl}
          title={pdfViewerData.title}
          onClose={() => setPdfViewerData(null)}
        />
      )}

    </div>
  );
};

// PDF Görüntüleme Modal
const PdfViewerModal = ({ url, downloadUrl, title, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <div className="flex items-center space-x-2">
            <a
              href={downloadUrl}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              İndir
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              Kapat
            </button>
          </div>
        </div>
        <div className="flex-1 bg-gray-100">
          <iframe
            src={url}
            title={title}
            className="w-full h-full border-0"
          />
        </div>
      </div>
    </div>
  );
};

// Atama Modal
const AssignModal = ({ workOrderId, companies, onClose, onSave }) => {
  const [companyId, setCompanyId] = useState('');
  const [contractFile, setContractFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId || !contractFile) {
      setError('Firma ve sözleşme seçilmelidir');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('workOrderId', workOrderId);
    formData.append('companyId', companyId);
    formData.append('contract', contractFile);

    try {
      await assignmentsAPI.create(formData);
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Firma Ata</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firma *</label>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Firma Seçin</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sözleşme (PDF) *</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setContractFile(e.target.files[0])}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
              İptal
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Atanıyor...' : 'Ata'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Süreç Modal
const ProcessModal = ({ workOrderId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    plannedStart: '',
    plannedEnd: '',
    requiresQc: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await processesAPI.create(workOrderId, formData);
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Süreç Ekle</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Süreç Adı *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: Kesim, Dikim, Paketleme"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç</label>
              <input
                type="date"
                value={formData.plannedStart}
                onChange={(e) => setFormData({ ...formData, plannedStart: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş</label>
              <input
                type="date"
                value={formData.plannedEnd}
                onChange={(e) => setFormData({ ...formData, plannedEnd: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="requiresQc"
              checked={formData.requiresQc}
              onChange={(e) => setFormData({ ...formData, requiresQc: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="requiresQc" className="text-sm text-gray-700">Bu süreç için QC gerekli</label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
              İptal
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Ekleniyor...' : 'Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// QC Talep Modal
const QcRequestModal = ({ process, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    processId: process.id,
    requestedDate: '',
    requestedTime: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await qcAPI.create(formData);
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">QC Talebi Oluştur</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <div className="p-6 bg-yellow-50 border-b border-yellow-100">
          <p className="text-yellow-700">
            <strong>{process.name}</strong> süreci için QC kontrolü gereklidir.
            Lütfen uygun bir tarih ve saat seçin.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tarih *</label>
              <input
                type="date"
                value={formData.requestedDate}
                onChange={(e) => setFormData({ ...formData, requestedDate: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Saat *</label>
              <input
                type="time"
                value={formData.requestedTime}
                onChange={(e) => setFormData({ ...formData, requestedTime: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
              İptal
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50">
              {loading ? 'Gönderiliyor...' : 'QC Talep Et'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkOrderDetail;
